/**
 * The effect condition registry.
 *
 * One import and one entry per condition, and nothing else. `EFFECT_CONDITION_ORDER` is the order
 * anything walking every condition uses, so a screen listing them and a test asserting over them
 * agree without either deciding.
 */

import { WHILE_DOWNED } from '@/sim/effects/effectConditions/whileDowned';
import { WHILE_HURT } from '@/sim/effects/effectConditions/whileHurt';
import { WHILE_MOVING } from '@/sim/effects/effectConditions/whileMoving';
import type { CharacterMoment } from '@/sim/effects/characterMoment';
import type { EffectCondition, EffectConditionId } from '@/sim/effects/effectConditionContract';

export const EFFECT_CONDITIONS = {
  whileMoving: WHILE_MOVING,
  whileDowned: WHILE_DOWNED,
  whileHurt: WHILE_HURT,
} as const satisfies Record<EffectConditionId, EffectCondition>;

/** Iteration order, and part of the determinism contract. */
export const EFFECT_CONDITION_ORDER: readonly EffectConditionId[] = [
  'whileMoving',
  'whileDowned',
  'whileHurt',
];

/** Condition lookup, total over the union so it never widens to `undefined`. */
export function effectCondition(id: EffectConditionId): EffectCondition {
  return EFFECT_CONDITIONS[id];
}

/** Whether `value` names a condition. Used when reading one back out of a save. */
export function isEffectConditionId(value: string): value is EffectConditionId {
  return Object.hasOwn(EFFECT_CONDITIONS, value);
}

/**
 * Whether a condition is in force. An absent condition always holds, which is what makes an
 * unconditional perk the same code path as a conditional one rather than a branch above it.
 */
export function conditionHolds(id: EffectConditionId | null, moment: CharacterMoment): boolean {
  return id === null || effectCondition(id).holds(moment);
}
